import React, { useState } from 'react';
import GitHubIcon from '@mui/icons-material/GitHub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { AnimatePresence, motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import cvm from '../assets/images/cvm-1.png';
import chatgptGif from '../assets/images/ChatGPT-custom2.gif';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';
import TechBadge from './ui/TechBadge';
import ArchitectureDiagram from './ui/ArchitectureDiagram';
import ScreenshotGallery, { ScreenshotItem } from './ui/ScreenshotGallery';

const imageMap: Record<string, string> = {
  cvm,
  chatgpt: chatgptGif,
};

type EngineeringDecision = { choice: string; reason: string };

type BusinessView = {
  headline: string;
  situation: string;
  painPoints: string[];
  approach: string;
  results: string[];
};

type ProjectData = (typeof setup.projects)[number] & {
  overview?: string;
  problem?: string;
  solution?: string;
  challenges?: string[];
  outcome?: string;
  lessonsLearned?: string[];
  architectureFlow?: string[];
  engineeringDecisions?: EngineeringDecision[];
  businessImpactItems?: string[];
  businessView?: BusinessView;
  screenshots?: ScreenshotItem[];
};

type CaseStudyView = 'business' | 'technical';

function estimateReadingTime(project: ProjectData, view: CaseStudyView): number {
  const bv = project.businessView;
  const text =
    view === 'business' && bv
      ? [bv.headline, bv.situation, bv.approach, ...bv.painPoints, ...bv.results].join(' ')
      : [
          project.overview,
          project.description,
          project.problem,
          project.solution,
          project.outcome,
          project.businessImpact,
          ...(project.businessImpactItems || []),
          ...(project.challenges || []),
          ...(project.lessonsLearned || []),
          ...(project.engineeringDecisions?.map((d) => `${d.choice} ${d.reason}`) || []),
        ]
          .filter(Boolean)
          .join(' ');
  const words = text.split(/\s+/).length;
  return Math.max(3, Math.ceil(words / 200));
}

function getMockupUrl(project: ProjectData): string {
  if (project.liveDemo) {
    try {
      return new URL(project.liveDemo).hostname;
    } catch {
      return 'app.example.com';
    }
  }
  return 'app.example.com';
}

function resolveImage(image: string | null | undefined): string | null {
  if (!image) return null;
  return imageMap[image] ?? image;
}

function getScreenshots(project: ProjectData): ScreenshotItem[] | null {
  if (project.screenshots?.length) {
    return project.screenshots.map((s) => ({
      label: s.label,
      image: resolveImage(s.image),
    }));
  }
  if (project.image && imageMap[project.image]) {
    return [{ label: project.title, image: imageMap[project.image] }];
  }
  return null;
}

function SectionBlock({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h4 className="text-sm font-semibold text-ink tracking-[-0.01em]">{title}</h4>
      </div>
      <div className="text-sm text-ink-muted leading-relaxed">{children}</div>
    </section>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: CaseStudyView;
  onChange: (view: CaseStudyView) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Case study perspective"
      className="inline-flex p-1 rounded-lg border border-line bg-canvas-muted"
    >
      {(
        [
          { id: 'business', label: 'What changed', short: 'Outcomes', icon: <BusinessCenterOutlinedIcon sx={{ fontSize: 16 }} /> },
          { id: 'technical', label: 'How it was built', short: 'Build', icon: <CodeOutlinedIcon sx={{ fontSize: 16 }} /> },
        ] as const
      ).map((tab) => {
        const active = view === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              active
                ? 'bg-surface text-ink shadow-soft border border-line'
                : 'text-ink-muted hover:text-ink border border-transparent'
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.short}</span>
          </button>
        );
      })}
    </div>
  );
}

function BusinessBody({ project }: { project: ProjectData }) {
  const bv = project.businessView;
  if (!bv) {
    return (
      <>
        {project.problem && (
          <SectionBlock title="The problem" icon={<LightbulbOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
            <p>{project.problem}</p>
          </SectionBlock>
        )}
        {project.solution && (
          <SectionBlock title="What we built" icon={<BuildOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
            <p>{project.solution}</p>
          </SectionBlock>
        )}
        {(project.businessImpactItems?.length || project.outcome) && (
          <SectionBlock title="Results" icon={<TrendingUpIcon sx={{ fontSize: 16 }} className="text-accent" />}>
            {project.businessImpactItems?.length ? (
              <ul className="space-y-2">
                {project.businessImpactItems.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
                      <CheckIcon sx={{ fontSize: 14 }} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p>{project.outcome}</p>
            )}
          </SectionBlock>
        )}
      </>
    );
  }

  return (
    <>
      <SectionBlock title="The situation" icon={<LightbulbOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
        <p>{bv.situation}</p>
      </SectionBlock>

      <SectionBlock title="What wasn't working" icon={<CloseIcon sx={{ fontSize: 16 }} className="text-red-500" />}>
        <ul className="space-y-2.5">
          {bv.painPoints.map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 flex items-center justify-center">
                <CloseIcon sx={{ fontSize: 14 }} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </SectionBlock>

      <SectionBlock title="How we solved it" icon={<BuildOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
        <p>{bv.approach}</p>
      </SectionBlock>

      <SectionBlock title="What changed for the business" icon={<TrendingUpIcon sx={{ fontSize: 16 }} className="text-accent" />}>
        <ul className="space-y-2.5">
          {bv.results.map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
                <CheckIcon sx={{ fontSize: 14 }} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </SectionBlock>
    </>
  );
}

function TechnicalBody({
  project,
  index,
}: {
  project: ProjectData;
  index: number;
}) {
  return (
    <>
      {project.problem && (
        <SectionBlock title="Problem" icon={<LightbulbOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
          <p>{project.problem}</p>
        </SectionBlock>
      )}

      {project.architectureFlow && project.architectureFlow.length > 0 && (
        <section
          className={`mb-8 p-5 sm:p-6 rounded-lg border bg-canvas-muted/50 ${
            index < 2 ? 'border-accent/25 ring-1 ring-accent/10' : 'border-line'
          }`}
        >
          <p className="text-sm font-semibold text-ink mb-5 text-center">Architecture</p>
          <ArchitectureDiagram steps={project.architectureFlow} />
        </section>
      )}

      {project.challenges && project.challenges.length > 0 && (
        <SectionBlock title="Challenges" icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
          <ul className="space-y-2 list-none">
            {project.challenges.map((c) => (
              <li key={c} className="pl-3 relative before:content-['–'] before:absolute before:left-0 before:text-ink-faint">
                {c}
              </li>
            ))}
          </ul>
        </SectionBlock>
      )}

      {project.engineeringDecisions && project.engineeringDecisions.length > 0 && (
        <SectionBlock title="Engineering Decisions" icon={<PsychologyOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
          <ul className="space-y-3">
            {project.engineeringDecisions.map((decision) => (
              <li key={decision.choice}>
                <span className="font-semibold text-ink">{decision.choice}</span>
                <span className="text-ink-muted"> {decision.reason}</span>
              </li>
            ))}
          </ul>
        </SectionBlock>
      )}

      {(project.businessImpactItems?.length || project.businessImpact || project.outcome) && (
        <SectionBlock title="Outcome" icon={<TrendingUpIcon sx={{ fontSize: 16 }} className="text-accent" />}>
          {project.businessImpactItems?.length ? (
            <ul className="space-y-2 mb-3">
              {project.businessImpactItems.map((item) => (
                <li key={item} className="pl-3 relative before:content-['–'] before:absolute before:left-0 before:text-accent">
                  {item}
                </li>
              ))}
            </ul>
          ) : project.businessImpact ? (
            <p className="mb-3">{project.businessImpact}</p>
          ) : null}
          {project.outcome && <p>{project.outcome}</p>}
        </SectionBlock>
      )}

      <section className="mb-6">
        <h4 className="text-sm font-semibold text-ink mb-3">Technology</h4>
        <div className="flex flex-wrap gap-2">
          {project.technologies.map((tech) => (
            <TechBadge key={tech} label={tech} />
          ))}
        </div>
      </section>

      {project.solution && (
        <SectionBlock title="Solution" icon={<BuildOutlinedIcon sx={{ fontSize: 16 }} className="text-ink-muted" />}>
          <p>{project.solution}</p>
        </SectionBlock>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted mb-3">Features</h4>
          <ul className="space-y-1.5">
            {project.features.map((feature) => (
              <li
                key={feature}
                className="text-sm text-ink-muted pl-3 relative before:content-['–'] before:absolute before:left-0 before:text-ink-faint"
              >
                {feature}
              </li>
            ))}
          </ul>
        </div>
        {project.lessonsLearned && project.lessonsLearned.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MenuBookOutlinedIcon sx={{ fontSize: 14 }} className="text-ink-muted" />
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">Lessons Learned</h4>
            </div>
            <ul className="space-y-1.5 list-none">
              {project.lessonsLearned.map((lesson) => (
                <li
                  key={lesson}
                  className="text-sm text-ink-muted pl-3 relative before:content-['–'] before:absolute before:left-0 before:text-ink-faint"
                >
                  {lesson}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}

function Project({
  limit,
  defaultView = 'business',
  showToggle = true,
}: {
  limit?: number;
  defaultView?: CaseStudyView;
  showToggle?: boolean;
} = {}) {
  const projects = (setup.projects as ProjectData[]).slice(0, limit);
  const [view, setView] = useState<CaseStudyView>(defaultView);
  const activeView = showToggle ? view : defaultView;

  return (
    <MotionSection id="case-studies" muted>
      <div className="flex flex-col gap-6 sm:gap-8 mb-10 sm:mb-12">
        <SectionHeader
          eyebrow="Proof"
          title="Case Studies"
          subtitle={
            activeView === 'business'
              ? 'The problems the business was facing—and what changed after we built the software.'
              : 'Architecture, decisions, challenges, and how the system was engineered.'
          }
        />
        {showToggle && <ViewToggle view={view} onChange={setView} />}
      </div>

      <div className="space-y-12">
        {projects.map((project, index) => {
          const readingTime = estimateReadingTime(project, activeView);
          const screenshots = getScreenshots(project);
          const mockupUrl = getMockupUrl(project);
          const overview =
            activeView === 'business' && project.businessView
              ? project.businessView.headline
              : project.overview || project.description;

          return (
            <motion.article
              key={project.title}
              initial={{ opacity: 0, y: 28, filter: 'blur(4px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: index * 0.04 }}
              className="ui-card ui-card-lift overflow-hidden !shadow-card"
            >
              {screenshots ? (
                <ScreenshotGallery items={screenshots} url={mockupUrl} gradient={project.gradient} />
              ) : (
                <div
                  className={`h-44 sm:h-52 bg-gradient-to-br ${project.gradient} flex items-center justify-center border-b border-line`}
                >
                  <span className="text-2xl sm:text-3xl font-semibold text-white/80 px-6 text-center">
                    {project.title}
                  </span>
                </div>
              )}

              <div className="p-5 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8 pb-6 border-b border-line">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-xl sm:text-2xl font-semibold text-ink tracking-[-0.02em]">
                        {project.title}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-xs text-ink-muted bg-canvas-muted px-2 py-0.5 rounded-md border border-line">
                        <ScheduleIcon sx={{ fontSize: 12 }} />
                        {readingTime} min read
                      </span>
                    </div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted mb-2">
                      {activeView === 'business' ? 'In plain terms' : 'Overview'}
                    </p>
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={`${project.title}-${activeView}-overview`}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.25 }}
                        className="text-ink-muted leading-relaxed text-[15px] max-w-2xl"
                      >
                        {overview}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noreferrer"
                        className="ui-btn-secondary !px-3 !py-1.5 !text-xs"
                      >
                        <GitHubIcon sx={{ fontSize: 16 }} />
                        GitHub
                      </a>
                    )}
                    {project.liveDemo && (
                      <a
                        href={project.liveDemo}
                        target="_blank"
                        rel="noreferrer"
                        className="ui-btn-primary !px-3 !py-1.5 !text-xs"
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                        Live Demo
                      </a>
                    )}
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${project.title}-${activeView}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.28 }}
                  >
                    {activeView === 'business' ? (
                      <BusinessBody project={project} />
                    ) : (
                      <TechnicalBody project={project} index={index} />
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.article>
          );
        })}
      </div>
    </MotionSection>
  );
}

export default Project;
